import { useEffect, useRef, useState } from "react";

const skillGroups = [
  {
    title: "Product & Strategy",
    icon: "ri-compass-3-line",
    tags: ["Product Management", "Roadmap Planning", "Design Thinking", "Customer Interviews", "OKRs", "Aha!", "Jira", "Miro", "Figma"],
  },
  {
    title: "Engineering & Architecture",
    icon: "ri-code-s-slash-line",
    tags: ["Kotlin", "Spring Boot", "Python", "SQL", "MongoDB", "AngularJS", "Microservices", "Agentic AI"],
  },
  {
    title: "Cloud & DevOps",
    icon: "ri-cloud-line",
    tags: ["AWS", "Terraform", "Docker", "CI/CD", "Infrastructure as Code", "Documentation as Code"],
  },
  {
    title: "Observability & Analytics",
    icon: "ri-bar-chart-box-line",
    tags: ["DataDog", "New Relic", "Splunk", "Google Analytics", "APM", "Data Analytics"],
  },
  {
    title: "Leadership & Agile",
    icon: "ri-team-line",
    tags: ["Extreme Ownership", "Scrum Master", "Agile Coaching", "Team Mentorship", "Cross-functional Leadership", "Capacity Planning"],
  },
  {
    title: "Growth & Marketing",
    icon: "ri-megaphone-line",
    tags: ["CRM", "Apollo", "MailChimp", "Salesflare", "SEO", "Email Campaigns"],
  },
];

function AccordionItem({ group, index, isOpen, onToggle }: {
  group: typeof skillGroups[0];
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(16px)";
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = `opacity 0.6s ease-out ${index * 0.07}s, transform 0.6s ease-out ${index * 0.07}s`;
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className="border-b transition-colors duration-200"
      style={{ borderColor: "rgba(255,255,255,0.07)" }}
    >
      <button
        className="w-full flex items-center justify-between gap-4 py-5 text-left cursor-pointer group"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div
            className="flex items-center justify-center flex-shrink-0 transition-colors duration-200"
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "9px",
              backgroundColor: isOpen ? "rgba(193,125,60,0.15)" : "rgba(255,255,255,0.05)",
              border: isOpen ? "1px solid rgba(193,125,60,0.25)" : "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <i className={`${group.icon} text-sm`} style={{ color: isOpen ? "#C17D3C" : "rgba(250,248,244,0.35)" }} />
          </div>
          <span
            className="font-dm font-medium text-[15px] transition-colors duration-200"
            style={{ color: isOpen ? "#FAF8F4" : "rgba(250,248,244,0.55)" }}
          >
            {group.title}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="font-dm text-[12px]" style={{ color: "rgba(250,248,244,0.2)" }}>
            {group.tags.length} skills
          </span>
          <i
            className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} text-sm`}
            style={{ color: isOpen ? "#C17D3C" : "rgba(250,248,244,0.25)" }}
          />
        </div>
      </button>

      {/* Tags panel */}
      <div
        className="overflow-hidden"
        style={{
          maxHeight: isOpen ? "200px" : "0",
          opacity: isOpen ? 1 : 0,
          transition: "max-height 0.35s ease, opacity 0.25s ease",
        }}
      >
        <div className="flex flex-wrap gap-2 pb-5">
          {group.tags.map((tag) => (
            <span
              key={tag}
              className="font-dm text-[12px]"
              style={{
                padding: "5px 12px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(250,248,244,0.55)",
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SkillsTools() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(24px)";
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = "opacity 0.8s ease-out, transform 0.8s ease-out";
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="skills" className="py-28 lg:py-36" style={{ backgroundColor: "#0E0B07" }}>
      <div className="mx-auto px-8 lg:px-16" style={{ maxWidth: "1200px" }}>

        {/* Header */}
        <div ref={headerRef} className="mb-14">
          <div className="flex items-center gap-4 mb-6">
            <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "#C17D3C" }}>03</span>
            <div style={{ width: "40px", height: "1px", backgroundColor: "#C17D3C", opacity: 0.5 }} />
            <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "rgba(250,248,244,0.35)" }}>Skills &amp; tools</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="font-lora font-semibold leading-[1.15] tracking-tight"
              style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)", color: "#FAF8F4" }}
            >
              The full stack<br />of what I bring.
            </h2>
            <p
              className="font-dm text-[14px] leading-[1.7]"
              style={{ color: "rgba(250,248,244,0.4)", maxWidth: "340px" }}
            >
              From product strategy and customer research to cloud infrastructure and CI/CD — I operate across the full spectrum.
            </p>
          </div>
        </div>

        {/* Two-column accordion layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16">
          <div>
            {skillGroups.slice(0, 3).map((group, i) => (
              <AccordionItem
                key={group.title}
                group={group}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              />
            ))}
          </div>
          <div>
            {skillGroups.slice(3).map((group, i) => (
              <AccordionItem
                key={group.title}
                group={group}
                index={i + 3}
                isOpen={openIndex === i + 3}
                onToggle={() => setOpenIndex(openIndex === i + 3 ? null : i + 3)}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
